'use client';

import { useTranslations } from 'next-intl';
import { SearchConfig, ServerActionName } from '@headless-workspace/config';
import { AsyncData, ResultEmpty, ResultFailure, ResultSuccess } from '@headless-workspace/core-domain';
import { StringHelpers } from '@headless-workspace/utils';
import {
    getSearchSuggestionsServerAction,
    RevalidationStrategy,
    SearchSuggestionsValue,
    useDebounceValue,
    VipBrandsValue,
} from '../../../../../src';
import { useSafeSWR } from '../../hooks/swr/useSafeSwr';

const SEARCH_SUGGESTIONS_KEY = 'search/suggestions/';
const MAX_SEARCHES_COUNT = 6;
const SEARCH_SUGGESTIONS_DEFAULT_ERROR = 'SEARCH_SUGGESTIONS_ERROR';

type UseSearchSuggestionsReturnType = AsyncData<SearchSuggestionsValue> & {
    shouldDisplayTags: boolean;
};

export const useSearchSuggestions = (searchInput: string, _: VipBrandsValue): UseSearchSuggestionsReturnType => {
    const t = useTranslations('Header.search.error');

    const debouncedInput = useDebounceValue(searchInput, SearchConfig.debounceTime);
    const { data, error, isLoading } = useSafeSWR(
        debouncedInput && `${SEARCH_SUGGESTIONS_KEY}${debouncedInput}`,
        ServerActionName.getSearchSuggestions,
        () => getSearchSuggestionsServerAction(debouncedInput),
        RevalidationStrategy.WarmDataPolicy,
    );

    if (!debouncedInput) {
        if (StringHelpers.isNotEmpty(searchInput)) {
            return { isLoading: true, result: ResultEmpty(), shouldDisplayTags: false };
        } else {
            return { isLoading: false, result: ResultEmpty(), shouldDisplayTags: false };
        }
    }

    if (!data || data.type === 'empty') {
        if (error) {
            return {
                isLoading,
                result: ResultFailure(
                    SEARCH_SUGGESTIONS_DEFAULT_ERROR,
                    error instanceof Error ? error.message : t('default'),
                ),
                shouldDisplayTags: false,
            };
        }

        return { isLoading, result: ResultEmpty(), shouldDisplayTags: false };
    }

    const { terms, categories, brands, products } = data.data;

    const shouldDisplayTags = terms.length > 0 || categories.length > 0 || brands.length > 0;

    if (products.length === 0 && !shouldDisplayTags) {
        return { isLoading, result: ResultEmpty(), shouldDisplayTags: false };
    }

    return {
        isLoading,
        result: ResultSuccess({
            terms,
            categories,
            brands,
            products: products.slice(0, MAX_SEARCHES_COUNT),
        }),
        shouldDisplayTags,
    };
};
